import { supabase } from "@/shared/lib/supabaseClient";
import type { Database, PessoaMinisterioStatus } from "@/shared/types/database.types";

/**
 * Camada de dados do Núcleo — Ministérios, Funções Ministeriais e vínculos
 * (03-Núcleo §5/§6, DA-001/DA-003/DA-004).
 *
 * A autorização mora no banco (RLS, migration 0016/0024):
 *   - Admin da plataforma gerencia ministérios, funções e vínculos (Nível 1);
 *   - Líder gerencia funções e vínculos do PRÓPRIO ministério (DA-004);
 *   - A pessoa vê os próprios vínculos (e aceita/recusa convites por RPC).
 * Este service não duplica essas regras — os erros de permissão do banco
 * viram mensagens amigáveis aqui.
 *
 * Padrão de consultas: composição sem embeds (Promise.all + dedupe em JS).
 */

export type Ministerio = Database["public"]["Tables"]["ministerios"]["Row"];
export type FuncaoMinisterial = Database["public"]["Tables"]["funcoes_ministeriais"]["Row"];

export interface MinisterioResumo {
  id: string;
  nome: string;
  descricao: string | null;
  ativo: boolean;
  /** Nomes dos líderes atuais (vínculo ativo em função com eh_lider). */
  lideres: string[];
  /** Pessoas distintas com vínculo ativo. */
  totalIntegrantes: number;
}

export interface IntegranteMinisterio {
  vinculoId: string;
  pessoaId: string;
  nome: string;
  funcaoId: string;
  funcaoNome: string;
  ehLider: boolean;
  /** A função tem o marcador "Gestor de elo" (pode_gerenciar_modulo). */
  ehGestor: boolean;
  status: Extract<PessoaMinisterioStatus, "ativo" | "convidado">;
}

export interface MinisterioDetalheDados {
  ministerio: Ministerio;
  funcoes: FuncaoMinisterial[];
  /** Vínculos ativos e convites pendentes (inativos ficam só como histórico). */
  integrantes: IntegranteMinisterio[];
  /**
   * Módulos ativos da igreja sob responsabilidade deste ministério (Skill
   * v3.0 — a antiga tela "Acessos" foi extinta; a visão "quem gerencia o
   * módulo" mora aqui: integrantes com função que tem pode_gerenciar_modulo).
   */
  modulosResponsavel: { codigo: string; nome: string }[];
}

export interface DadosFuncao {
  nome: string;
  ehLider: boolean;
  podeGerenciarModulo: boolean;
}

export interface MinisterioLiderado {
  id: string;
  nome: string;
}

/** Nome padrão da função de liderança criada automaticamente (DA-004). */
const NOME_FUNCAO_LIDER = "Líder";

async function igrejaAtualId(): Promise<string> {
  const { data, error } = await supabase.from("igrejas").select("id").limit(1).single();
  if (error || !data) throw new Error("Não foi possível identificar a igreja.");
  return data.id;
}

/**
 * Ministérios ATIVOS em que a pessoa tem vínculo ativo com uma função que tem o marcador pedido
 * (`eh_lider` ou `pode_gerenciar_modulo`).
 */
async function ministeriosAtivosPorMarcador(
  pessoaId: string,
  marcador: "eh_lider" | "pode_gerenciar_modulo",
): Promise<MinisterioLiderado[]> {
  const { data: vinculos, error } = await supabase
    .from("pessoa_ministerio")
    .select("ministerio_id, funcao_id")
    .eq("pessoa_id", pessoaId)
    .eq("status", "ativo");
  if (error) throw new Error("Não foi possível carregar seus ministérios.");
  if (!vinculos?.length) return [];

  const { data: funcoes, error: erroFuncoes } = await supabase
    .from("funcoes_ministeriais")
    .select("id")
    .in("id", [...new Set(vinculos.map((v) => v.funcao_id))])
    .eq(marcador, true);
  if (erroFuncoes) throw new Error("Não foi possível verificar suas funções.");
  const funcoesComMarcador = new Set((funcoes ?? []).map((f) => f.id));

  const ministerioIds = [
    ...new Set(
      vinculos.filter((v) => funcoesComMarcador.has(v.funcao_id)).map((v) => v.ministerio_id),
    ),
  ];
  if (!ministerioIds.length) return [];

  const { data: ministerios, error: erroMinisterios } = await supabase
    .from("ministerios")
    .select("id, nome")
    .in("id", ministerioIds)
    .eq("ativo", true)
    .order("nome");
  if (erroMinisterios) throw new Error("Não foi possível carregar seus ministérios.");
  return ministerios ?? [];
}

export const ministeriosService = {
  /**
   * Ministérios ATIVOS que a pessoa lidera (vínculo ativo em função com
   * eh_lider). Alimenta o card "Meu ministério" no Início — a porta de
   * entrada do líder que não é admin (DA-004).
   */
  async meusMinisteriosLiderados(pessoaId: string): Promise<MinisterioLiderado[]> {
    return ministeriosAtivosPorMarcador(pessoaId, "eh_lider");
  },

  /**
   * Ministérios ATIVOS em que a pessoa tem uma função com o marcador "Gestor de elo"
   * (pode_gerenciar_modulo), em vínculo ativo. Alimenta o cartão "Elos que você gerencia" do
   * Início e as lições da Ajuda. Só orienta: quem decide o que ela pode fazer é o banco.
   */
  async meusMinisteriosComoGestor(pessoaId: string): Promise<MinisterioLiderado[]> {
    return ministeriosAtivosPorMarcador(pessoaId, "pode_gerenciar_modulo");
  },

  /** Todos os ministérios da igreja, com líderes e total de integrantes. */
  async listar(): Promise<MinisterioResumo[]> {
    const { data: ministerios, error } = await supabase
      .from("ministerios")
      .select("id, nome, descricao, ativo")
      .order("nome");
    if (error) throw new Error("Não foi possível carregar os ministérios.");
    if (!ministerios?.length) return [];

    const [vinculosRes, funcoesRes] = await Promise.all([
      supabase
        .from("pessoa_ministerio")
        .select("pessoa_id, ministerio_id, funcao_id")
        .eq("status", "ativo"),
      supabase.from("funcoes_ministeriais").select("id, eh_lider"),
    ]);
    if (vinculosRes.error || funcoesRes.error) {
      throw new Error("Não foi possível carregar os integrantes dos ministérios.");
    }
    const vinculos = vinculosRes.data ?? [];
    const funcaoEhLider = new Map((funcoesRes.data ?? []).map((f) => [f.id, f.eh_lider]));

    const liderPessoaIds = [
      ...new Set(vinculos.filter((v) => funcaoEhLider.get(v.funcao_id)).map((v) => v.pessoa_id)),
    ];
    let pessoas: { id: string; nome_completo: string }[] = [];
    if (liderPessoaIds.length) {
      const { data, error: erroPessoas } = await supabase
        .from("pessoas")
        .select("id, nome_completo")
        .in("id", liderPessoaIds);
      if (erroPessoas) throw new Error("Não foi possível carregar os líderes.");
      pessoas = data ?? [];
    }
    const nomePorPessoa = new Map(pessoas.map((p) => [p.id, p.nome_completo]));

    return ministerios.map((m) => {
      const doMinisterio = vinculos.filter((v) => v.ministerio_id === m.id);
      const lideres = [
        ...new Set(
          doMinisterio
            .filter((v) => funcaoEhLider.get(v.funcao_id))
            .map((v) => nomePorPessoa.get(v.pessoa_id) ?? "(pessoa não encontrada)"),
        ),
      ].sort((a, b) => a.localeCompare(b));
      return {
        id: m.id,
        nome: m.nome,
        descricao: m.descricao,
        ativo: m.ativo,
        lideres,
        totalIntegrantes: new Set(doMinisterio.map((v) => v.pessoa_id)).size,
      };
    });
  },

  /** Detalhe de um ministério: dados + funções + integrantes/convites + módulos. */
  async detalhe(ministerioId: string): Promise<MinisterioDetalheDados> {
    const [ministerioRes, funcoesRes, vinculosRes, ativacoesRes] = await Promise.all([
      supabase.from("ministerios").select("*").eq("id", ministerioId).single(),
      supabase
        .from("funcoes_ministeriais")
        .select("*")
        .eq("ministerio_id", ministerioId)
        .order("nome"),
      supabase
        .from("pessoa_ministerio")
        .select("id, pessoa_id, funcao_id, status")
        .eq("ministerio_id", ministerioId)
        .in("status", ["ativo", "convidado"]),
      supabase
        .from("igreja_modulos_ministerios")
        .select("modulo_codigo")
        .eq("ministerio_id", ministerioId),
    ]);
    if (ministerioRes.error || !ministerioRes.data) {
      throw new Error("Não foi possível carregar o ministério.");
    }
    if (funcoesRes.error || vinculosRes.error || ativacoesRes.error) {
      throw new Error("Não foi possível carregar as funções e integrantes.");
    }

    // Módulos sob responsabilidade (nomes do catálogo). Ministério 1:N com
    // módulo agora (migration 0027, tabela igreja_modulos_ministerios) —
    // filtra os códigos vinculados a este ministério e depois só mantém os
    // módulos que estão ativos (sem embed, seguindo o padrão do arquivo).
    const codigosVinculados = [
      ...new Set((ativacoesRes.data ?? []).map((a) => a.modulo_codigo)),
    ];
    let modulosResponsavel: { codigo: string; nome: string }[] = [];
    if (codigosVinculados.length) {
      const [catalogoRes, ativosRes] = await Promise.all([
        supabase.from("modulos").select("codigo, nome").in("codigo", codigosVinculados),
        supabase
          .from("igreja_modulos")
          .select("modulo_codigo")
          .in("modulo_codigo", codigosVinculados)
          .eq("ativo", true),
      ]);
      if (catalogoRes.error || ativosRes.error) {
        throw new Error("Não foi possível carregar os elos do ministério.");
      }
      const codigosAtivos = new Set((ativosRes.data ?? []).map((a) => a.modulo_codigo));
      modulosResponsavel = (catalogoRes.data ?? [])
        .filter((m) => codigosAtivos.has(m.codigo))
        .map((m) => ({ codigo: m.codigo, nome: m.nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome));
    }
    const funcoes = funcoesRes.data ?? [];
    const vinculos = vinculosRes.data ?? [];
    const funcaoPorId = new Map(funcoes.map((f) => [f.id, f]));

    const pessoaIds = [...new Set(vinculos.map((v) => v.pessoa_id))];
    let pessoas: { id: string; nome_completo: string }[] = [];
    if (pessoaIds.length) {
      // Admin vê todas; o líder vê as pessoas do próprio ministério pela
      // policy "Lider ve pessoas dos proprios ministerios" (migration 0024).
      const { data, error } = await supabase
        .from("pessoas")
        .select("id, nome_completo")
        .in("id", pessoaIds);
      if (error) throw new Error("Não foi possível carregar os nomes dos integrantes.");
      pessoas = data ?? [];
    }
    const nomePorPessoa = new Map(pessoas.map((p) => [p.id, p.nome_completo]));

    const integrantes: IntegranteMinisterio[] = vinculos
      .map((v) => {
        const funcao = funcaoPorId.get(v.funcao_id);
        return {
          vinculoId: v.id,
          pessoaId: v.pessoa_id,
          nome: nomePorPessoa.get(v.pessoa_id) ?? "(pessoa não encontrada)",
          funcaoId: v.funcao_id,
          funcaoNome: funcao?.nome ?? "(função removida)",
          ehLider: funcao?.eh_lider ?? false,
          ehGestor: funcao?.pode_gerenciar_modulo ?? false,
          status: v.status as IntegranteMinisterio["status"],
        };
      })
      .sort((a, b) => a.nome.localeCompare(b.nome));

    return { ministerio: ministerioRes.data, funcoes, integrantes, modulosResponsavel };
  },

  /**
   * Cria o ministério e já semeia a função "Líder" (eh_lider), para que
   * nomear o líder (DA-004) nunca dependa de configuração manual prévia.
   */
  async criar(nome: string, descricao: string | null): Promise<Ministerio> {
    const igrejaId = await igrejaAtualId();
    const { data, error } = await supabase
      .from("ministerios")
      .insert({ igreja_id: igrejaId, nome, descricao })
      .select()
      .single();
    if (error || !data) {
      if (error?.code === "23505") {
        throw new Error("Já existe um ministério com esse nome.");
      }
      throw new Error("Não foi possível criar o ministério.");
    }

    const { error: erroFuncao } = await supabase
      .from("funcoes_ministeriais")
      .insert({ ministerio_id: data.id, nome: NOME_FUNCAO_LIDER, eh_lider: true });
    if (erroFuncao) {
      throw new Error(
        'Ministério criado, mas não foi possível criar a função "Líder". Crie-a manualmente na tela do ministério.',
      );
    }
    return data;
  },

  async atualizar(id: string, dados: { nome: string; descricao: string | null }): Promise<void> {
    const { error } = await supabase.from("ministerios").update(dados).eq("id", id);
    if (error) {
      if (error.code === "23505") throw new Error("Já existe um ministério com esse nome.");
      throw new Error("Não foi possível atualizar o ministério.");
    }
  },

  /** Desativar preserva funções, integrantes e histórico (DA-004). */
  async definirAtivo(id: string, ativo: boolean): Promise<void> {
    const { error } = await supabase.from("ministerios").update({ ativo }).eq("id", id);
    if (error) {
      throw new Error(
        ativo ? "Não foi possível reativar o ministério." : "Não foi possível desativar o ministério.",
      );
    }
  },

  /**
   * EXCLUI o ministério de vez (admin) — só quando ele nunca foi vinculado a
   * um módulo (`igreja_modulos.ministerio_id`). Diferente de desativar:
   * funções e vínculos de integrantes são apagados em cascata (migration
   * 0016), sem histórico. Para ministério com módulo, o caminho é desativar.
   */
  async excluir(id: string): Promise<void> {
    const { data: vinculoModulo, error: erroBusca } = await supabase
      .from("igreja_modulos")
      .select("modulo_codigo")
      .eq("ministerio_id", id)
      .limit(1)
      .maybeSingle();
    if (erroBusca) throw new Error("Não foi possível verificar os elos do ministério.");
    if (vinculoModulo) {
      throw new Error(
        "Este ministério é responsável por um elo. Remova o vínculo na tela Elos da igreja antes de excluir — ou apenas desative o ministério.",
      );
    }

    const { error } = await supabase.from("ministerios").delete().eq("id", id);
    if (error) {
      // 23503: o banco é o guarda final caso um vínculo de módulo tenha
      // surgido entre a verificação e o delete.
      if (error.code === "23503") {
        throw new Error(
          "Este ministério é responsável por um elo. Remova o vínculo na tela Elos da igreja antes de excluir.",
        );
      }
      throw new Error("Não foi possível excluir o ministério.");
    }
  },

  // --- Funções Ministeriais (DA-001) ---------------------------------------

  async criarFuncao(ministerioId: string, dados: DadosFuncao): Promise<void> {
    const { error } = await supabase.from("funcoes_ministeriais").insert({
      ministerio_id: ministerioId,
      nome: dados.nome,
      eh_lider: dados.ehLider,
      pode_gerenciar_modulo: dados.podeGerenciarModulo,
    });
    if (error) {
      if (error.code === "23505") {
        throw new Error("Já existe uma função com esse nome neste ministério.");
      }
      throw new Error("Não foi possível criar a função.");
    }
  },

  async atualizarFuncao(funcaoId: string, dados: DadosFuncao): Promise<void> {
    const { error } = await supabase
      .from("funcoes_ministeriais")
      .update({
        nome: dados.nome,
        eh_lider: dados.ehLider,
        pode_gerenciar_modulo: dados.podeGerenciarModulo,
      })
      .eq("id", funcaoId);
    if (error) {
      if (error.code === "23505") {
        throw new Error("Já existe uma função com esse nome neste ministério.");
      }
      throw new Error("Não foi possível atualizar a função.");
    }
  },

  /**
   * Exclui a função. O banco impede a exclusão se ela já foi usada em algum
   * vínculo (inclusive histórico inativo) — nesse caso, oriente a renomear.
   */
  async excluirFuncao(funcaoId: string): Promise<void> {
    const { error } = await supabase.from("funcoes_ministeriais").delete().eq("id", funcaoId);
    if (error) {
      if (error.code === "23503") {
        throw new Error(
          "Esta função já foi usada por integrantes (o histórico é preservado). Se precisar, renomeie-a em vez de excluir.",
        );
      }
      throw new Error("Não foi possível excluir a função.");
    }
  },

  // --- Integrantes (DA-003) -------------------------------------------------

  /**
   * Cria/reativa o vínculo pessoa×ministério×função. `status: "ativo"` é o
   * cadastro direto pelo admin (DA-003.2); `status: "convidado"` é o convite
   * (DA-003.1) — a pessoa aceita/recusa depois (RPCs da migration 0024).
   */
  async adicionarIntegrante(
    pessoaId: string,
    ministerioId: string,
    funcaoId: string,
    status: Extract<PessoaMinisterioStatus, "ativo" | "convidado">,
  ): Promise<void> {
    const { data: existente, error: erroBusca } = await supabase
      .from("pessoa_ministerio")
      .select("id, status")
      .eq("pessoa_id", pessoaId)
      .eq("ministerio_id", ministerioId)
      .eq("funcao_id", funcaoId)
      .maybeSingle();
    if (erroBusca) throw new Error("Não foi possível verificar o vínculo existente.");

    if (existente) {
      if (existente.status === "ativo") {
        throw new Error("Esta pessoa já é integrante com essa função.");
      }
      if (existente.status === "convidado") {
        throw new Error("Esta pessoa já tem um convite pendente para essa função.");
      }
      const { error } = await supabase
        .from("pessoa_ministerio")
        .update({ status })
        .eq("id", existente.id);
      if (error) throw new Error("Não foi possível reativar o vínculo.");
      return;
    }

    const { error } = await supabase.from("pessoa_ministerio").insert({
      pessoa_id: pessoaId,
      ministerio_id: ministerioId,
      funcao_id: funcaoId,
      status,
    });
    if (error) throw new Error("Não foi possível adicionar o integrante.");
  },

  /** Inativa o vínculo (remove/cancela convite) — histórico preservado. */
  async removerIntegrante(vinculoId: string): Promise<void> {
    const { error } = await supabase
      .from("pessoa_ministerio")
      .update({ status: "inativo" })
      .eq("id", vinculoId);
    if (error) throw new Error("Não foi possível remover o integrante.");
  },

  /**
   * A39 (reteste 23-24/07/2026): quando a pessoa tem 2+ funções no mesmo
   * ministério, o botão geral de remover inativa TODOS os vínculos dela de
   * uma vez (desvincula do ministério por completo), em vez de exigir
   * remover função por função. Continua sendo um UPDATE de status —
   * histórico preservado, igual removerIntegrante.
   */
  async removerTodosVinculos(ministerioId: string, pessoaId: string): Promise<void> {
    const { error } = await supabase
      .from("pessoa_ministerio")
      .update({ status: "inativo" })
      .eq("ministerio_id", ministerioId)
      .eq("pessoa_id", pessoaId)
      .in("status", ["ativo", "convidado"]);
    if (error) throw new Error("Não foi possível remover o integrante.");
  },

  /**
   * Nomeia/troca o líder (DA-004 v2 — admin OU o próprio líder, Skill v3.0):
   * inativa os vínculos ativos nas funções de liderança e ativa o da nova
   * pessoa. Usa a primeira função com eh_lider (criando "Líder" se faltar).
   */
  async definirLider(ministerioId: string, pessoaId: string): Promise<void> {
    const { data: funcoes, error: erroFuncoes } = await supabase
      .from("funcoes_ministeriais")
      .select("id, nome")
      .eq("ministerio_id", ministerioId)
      .eq("eh_lider", true)
      .order("created_at");
    if (erroFuncoes) throw new Error("Não foi possível carregar as funções de liderança.");

    let funcaoLiderId = funcoes?.[0]?.id;
    if (!funcaoLiderId) {
      const { data: nova, error: erroCriar } = await supabase
        .from("funcoes_ministeriais")
        .insert({ ministerio_id: ministerioId, nome: NOME_FUNCAO_LIDER, eh_lider: true })
        .select("id")
        .single();
      if (erroCriar || !nova) throw new Error('Não foi possível criar a função "Líder".');
      funcaoLiderId = nova.id;
    }

    const funcaoLiderIds = funcoes?.length ? funcoes.map((f) => f.id) : [funcaoLiderId];

    // Encerra a liderança atual (exceto se já for a mesma pessoa).
    const { data: atuais, error: erroAtuais } = await supabase
      .from("pessoa_ministerio")
      .select("id, pessoa_id")
      .eq("ministerio_id", ministerioId)
      .in("funcao_id", funcaoLiderIds)
      .eq("status", "ativo");
    if (erroAtuais) throw new Error("Não foi possível verificar o líder atual.");

    const encerrar = (atuais ?? []).filter((v) => v.pessoa_id !== pessoaId).map((v) => v.id);
    if (encerrar.length) {
      const { error } = await supabase
        .from("pessoa_ministerio")
        .update({ status: "inativo" })
        .in("id", encerrar);
      if (error) throw new Error("Não foi possível encerrar a liderança atual.");
    }

    if ((atuais ?? []).some((v) => v.pessoa_id === pessoaId)) return;
    await this.adicionarIntegrante(pessoaId, ministerioId, funcaoLiderId, "ativo");
  },
};
